import { useEffect, useState } from 'react'
import { getBuildInfo, type BuildInfoResponse } from '../api/buildInfo'

// Shows what the API reports about its own build: when it was compiled and from
// which commit. Handy for confirming which version is actually deployed.
//
// This never blocks the page it sits on. If the call fails it just says so —
// the login form has to keep working when the build-info endpoint doesn't.
export function BuildInfo() {
  const [info, setInfo] = useState<BuildInfoResponse | null>(null)
  const [failed, setFailed] = useState(false)

  useEffect(() => {
    // Guard against setting state after unmount (React 18 StrictMode double-invokes
    // effects in dev, and the user may sign in before this resolves).
    let cancelled = false

    getBuildInfo()
      .then((data) => {
        if (!cancelled) setInfo(data)
      })
      .catch(() => {
        if (!cancelled) setFailed(true)
      })

    return () => {
      cancelled = true
    }
  }, [])

  if (failed) {
    return <p className="build-info build-info--empty">Build info unavailable</p>
  }

  if (!info) {
    return <p className="build-info build-info--empty">Loading build info…</p>
  }

  return (
    <dl className="build-info">
      <div className="build-info__row">
        <dt>Built</dt>
        <dd>{formatBuildTime(info.buildTimeUtc)}</dd>
      </div>
      <div className="build-info__row">
        <dt>Assembly</dt>
        <dd>{info.assemblyVersion}</dd>
      </div>
      <div className="build-info__row">
        <dt>Version</dt>
        <dd className="build-info__mono">{info.informationalVersion}</dd>
      </div>
      <div className="build-info__row">
        <dt>Commit</dt>
        <dd className="build-info__mono" title={info.commitSha ?? undefined}>
          {shortSha(info.commitSha)}
        </dd>
      </div>
    </dl>
  )
}

// The API sends UTC; show it in the viewer's local timezone, which is what you
// want when answering "did my deploy actually land?".
function formatBuildTime(iso: string | null): string {
  if (!iso) return 'unknown'

  const date = new Date(iso)

  return Number.isNaN(date.getTime()) ? 'unknown' : date.toLocaleString()
}

// Full sha is on the title attribute; 7 chars is the conventional short form.
function shortSha(sha: string | null): string {
  return sha ? sha.slice(0, 7) : 'unknown'
}
